import classNames from "classnames"

import { Color } from "../../../constants/V2/color"
import { trackSegmentEvent } from "../../../utils/analytics"
import Link from "../../elements/Link"
import Typography from "../../elements/V2/Typography"

import ImageLink from "@components/elements/ImageLink"
import TextButton from "@components/elements/V2/Buttons/TextButton"
import GatsbyStoryblokImage from "@components/elements/V2/GatsbyStoryblokImage"
import ArrowIcon from "@components/elements/V2/Icons/ArrowIcon"
import React, { useCallback } from "react"

interface LinksMenu {
  type: "link"
  title: string
  links: ImageLinks[]
  ctaText?: string
  ctaLink?: string | YocoCom.YocoLink
}

interface DisplayLinkMenu {
  type: "display"
  title: string
  link: ImageLinks
  hoverZoom?: boolean
}

interface ImageLinks {
  label: string
  description: string
  link: string | YocoCom.YocoLink
  image?: string
  imageSize?: "small" | "large"
  badgeText?: string
  badgeColor?: Color
}

const getTrackingPath = (link: string | YocoCom.YocoLink) => {
  return typeof link === "string" ? link : link.url
}

export type SubMenuBlockProps = LinksMenu | DisplayLinkMenu

type Props = SubMenuBlockProps & {
  className?: string
  onClick?: () => void
}

const SubMenuBlock = ({ className, onClick, ...props }: Props) => {
  const animateClassnames =
    "transition-all duration-200 lg:translate-x-[-20px] lg:group-hover:translate-x-0"

  const handleClick = useCallback(
    // eslint-disable-next-line @typescript-eslint/member-delimiter-style
    ({ label, link }: { label: string; link: string | YocoCom.YocoLink }) => {
      trackSegmentEvent("main_navigation_link", {
        anchorText: label,
        path: getTrackingPath(link),
      })
      onClick?.()
    },
    [onClick]
  )

  return (
    <div className={classNames(className, "col-span-6 pt-[6px] sm:pt-0")}>
      <Typography
        color={Color.Charcoal}
        font="grotesk"
        weight="book"
        size="subscript-sm"
        className="mb-16 uppercase tracking-wide"
        text={props.title}
      />

      {props.type === "link" ? (
        <div className="flex flex-col gap-[12px]">
          {props.links.map((link, index) => {
            return (
              <ImageLink
                key={index}
                text={link.label}
                description={link.description}
                badgeText={link.badgeText}
                badgeColor={link.badgeColor}
                imageAlt={link.label}
                imageUrl={link.image}
                onClick={() =>
                  handleClick({ label: link.label, link: link.link })
                }
                link={link.link}
                size={link.imageSize === "small" ? "small" : "large"}
              />
            )
          })}

          {props.ctaLink && props.ctaText && (
            <TextButton
              text={props.ctaText}
              style="icon"
              linkUrl={props.ctaLink}
              icon={<ArrowIcon width="12" height="10" />}
              color={Color.Black}
              textSize="body-md-fixed"
              className="mt-8"
            />
          )}
        </div>
      ) : props.type === "display" ? (
        <Link
          link={props.link.link}
          onClick={() =>
            handleClick({ label: props.link.label, link: props.link.link })
          }
          className="group flex w-fit items-center gap-20 lg:flex-col lg:items-start"
        >
          {props.link.image && (
            <div className="min-w-min overflow-hidden rounded-[4px]">
              <GatsbyStoryblokImage
                image={props.link.image}
                alt={props.link.label}
                aspectRatio="16:9"
                className={classNames(
                  {
                    "transform transition-transform duration-300 group-hover:scale-110":
                      props.hoverZoom,
                  },
                  "h-[57px] w-[74px] lg:h-104 lg:w-[180px]"
                )}
              />
            </div>
          )}
          <div className="flex h-[57px] max-w-[200px] flex-col justify-between gap-1 lg:h-auto lg:flex-col-reverse lg:justify-normal">
            <Typography
              color={Color.Charcoal}
              font="grotesk"
              weight="book"
              size="subscript-sm"
              text={props.link.description}
              className="opacity-70"
            />
            <div className="flex items-center gap-[5px]">
              <Typography
                color={Color.Charcoal}
                font="grotesk"
                weight="medium"
                size="body-md-fixed"
                text={props.link.label}
                className={classNames(
                  "relative duration-200 ease-in-out group-hover:text-blue"
                )}
              />
              <ArrowIcon
                className={classNames(
                  "-mt-[2px] group-hover:text-blue lg:opacity-0 lg:group-hover:opacity-100",
                  animateClassnames
                )}
                width="12"
                height="10"
              />
            </div>
          </div>
        </Link>
      ) : null}
    </div>
  )
}

export default SubMenuBlock
